import { FormSearchInput } from "@/components/Form/FormSearchInput";
import { DisciplinasSchema, MatriculaAlunoSchema } from "@/schema/matriculaAlunoSchema";
import { API_CONSTANT_MAP } from "@/services/paths";
import { getTurmaById } from "@/services/turma";
import { useFormContext } from "react-hook-form";
import { DisciplinasType, EspecificaTableType, EspecificaType, ServicoType } from "../types";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Divider, Form, GetRef, Input, InputNumber, InputNumberProps, InputRef, Modal, Row, Select, Table, TableColumnsType, Typography } from "antd";
import { useUpdateEffect } from "@/hooks/useUpdateEffect";
import { Orcamento } from "../Reports/Orcamento";
import type { ParcelasInfo } from "../Reports/Orcamento";
import { useParams } from "react-router-dom";
import { ColumnsType, TableProps } from "antd/es/table";
import { set } from "lodash";
import { createNumberMask } from "text-mask-addons";
import MaskedInput from "react-text-mask";
import { Container } from "../styles";
import React from "react";

type FormInstance<T> = GetRef<typeof Form<T>>;

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface EditableRowProps {
  index: number;
}

const currencyMask = createNumberMask({
  prefix: 'R$ ',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: '.',
  allowDecimal: true,
  decimalSymbol: ',',
  decimalLimit: 2, // Limita a 2 casas decimais
  integerLimit: null, // Limita o número de dígitos inteiros
  allowNegative: false,
  allowLeadingZeroes: false,
});

const percentMask = createNumberMask({
  prefix: '% ',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: '.',
  allowDecimal: true,
  decimalSymbol: ',',
  decimalLimit: 2, // Limita a 2 casas decimais
  integerLimit: 2, // Limita o número de dígitos inteiros
  allowNegative: false,
  allowLeadingZeroes: false,
});

type Props = {
  current: number;
  disabled?: boolean;
};

const { Text, Link } = Typography;

interface Item {
  key: string;
  disciplinaId: number;
  tipo: string;
  nome: string;
  valor: number;
  percDesconto: number;
  valorDesconto: number;
  valorFinal: number;
  disableCheck: boolean;
  selecionado: boolean;
  descontoManual?: boolean;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingInlineEnd: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

type ColumnTypes = Exclude<TableProps<EspecificaTableType>['columns'], undefined>;

const Disciplinas: React.FC<Props> = ({ current, disabled = false }) => {

  const [especificasTable, setEspecificasTable] = useState<EspecificaTableType[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [disciplinasType, setDisciplinasType] = useState<DisciplinasType>();
  const [countDescontoSelected, setCountDescontoSelected] = useState<number>(0);
  const [taxaMatricula, setTaxaMatricula] = useState<number>(0);
  const [taxaMatriculaSemDesconto, setTaxaMatriculaSemDesconto] = useState<number>(0);
  const [pcDescontoAdicional, setPcDescontoAdicional] = useState<number>(0);
  const [valorDescontoAdicional, setValorDescontoAdicional] = useState<number>(0);
  const [possuiRelacionadasSelecionado, setPossuiRelacionadasSelecionado] = useState<boolean>(false);
  const [descontoAdicionalHabilitado, setDescontoAdicionalHabilitado] = useState<boolean>(true);
  const [disableValorDesconto, setDisableValorDesconto] = useState<boolean>(false);
  const [disablePcDesconto, setDisablePcDesconto] = useState<boolean>(false);
  const [valorFinal, setValorFinal] = useState<number>(0);
  const [isModalOrcamentoOpen, setIsModalOrcamentoOpen] = useState<boolean>(false);
  const [isModalParcelasOpen, setIsModalParcelasOpen] = useState<boolean>(false);
  const [parcelasOrcamento, setParcelasOrcamento] = useState<ParcelasInfo | null>(null);
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<string>('avista');
  const [enableCabine, setEnableCabine] = useState<boolean>(false);
  const [numeroCabine, setNumeroCabine] = useState<number | undefined>();
  const [enableArmario, setEnableArmario] = useState<boolean>(false);
  const [numeroArmario, setNumeroArmario] = useState<number | undefined>();
  const [count, setCount] = useState(2);
  const [editandoDescontoMatricula, setEditandoDescontoMatricula] = useState<boolean>(false);
  const [descontoMatriculaManual, setDescontoMatriculaManual] = useState<number | null>(null);

  const isHydrating = useRef(false);
  const justHydrated = useRef(false);
  const previousTurmaIdRef = useRef<any>(undefined);

  const { id } = useParams();
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [openCabine, setOpenCabine] = useState(false);
  const [openArmario, setOpenArmario] = useState(false);

  const {
    getValues,
    watch,
    setValue,
  } = useFormContext<any>();

  const sanitizeNumber = (value: unknown): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const safePercentage = (discountValue: number, baseValue: number): number => {
    const safeDiscount = sanitizeNumber(discountValue);
    const safeBase = sanitizeNumber(baseValue);
    if (safeBase <= 0) {
      return 0;
    }
    return sanitizeNumber((safeDiscount / safeBase) * 100);
  };

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
    {
      title: 'Especifica',
      dataIndex: 'nome',
      render: (text: string) => <a>{text}</a>,
    },
    {
      key: 'valor',
      title: 'Valor sem Desconto',
      dataIndex: 'valor',
      render: (_, { valor }) => { return valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) },
      align: 'right',
    },
    {
      title: '% Desconto',
      dataIndex: 'percDesconto',
      render: (_, { percDesconto }) => { return percDesconto.toLocaleString('pt-br', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
      align: 'right',
    },
    {
      title: 'Desconto',
      dataIndex: 'valorDesconto',
      render: (_, { valorDesconto }) => { return valorDesconto.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) },
      align: 'right',
      editable: true,
    },
    {
      title: 'Proposta Valor Final',
      dataIndex: 'valorFinal',
      render: (_, { valorFinal }) => { return valorFinal.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) },
      align: 'right',
    },
  ];

  const handleSave = (row: EspecificaTableType) => {
    const newData = [...especificasTable];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    // Normaliza valorDesconto vindo do input (string com R$ etc.)
    const valorDescontoNum = Number(
      row.valorDesconto?.toString().replace('R$', '').replace(/\./g, '').replace(',', '.')
    ) || 0;
    // Linha desmarcada não entra no total — ignora o desconto digitado nela,
    // senão ela passaria a somar no Valor Total sem o checkbox marcado.
    if (!selectedRowKeys.includes(item.key)) {
      return;
    }
    // Limita ao valor da disciplina
    const valorDescontoLimitado = Math.min(Math.max(valorDescontoNum, 0), item.valor);
    const updated: EspecificaTableType = {
      ...item,
      valorDesconto: valorDescontoLimitado,
      valorDescontoManual: valorDescontoLimitado,
      percDesconto: item.valor > 0 ? (valorDescontoLimitado / item.valor) * 100 : 0,
      valorFinal: item.valor - valorDescontoLimitado,
      descontoManual: true,
      selecionado: true,
    };
    newData.splice(index, 1, updated);
    setEspecificasTable(newData);
    // Reaplica o recálculo geral: o desconto manual vira a base da linha e o
    // desconto adicional (se houver) é aplicado por cima dela em atualizaTable.
    atualizaTable(newData.filter(d => selectedRowKeys.includes(d.key)));
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable || disabled) {
      return col;
    }
    return {
      ...col,
      onCell: (record: EspecificaTableType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const pcDescontoAdicionalDefault = getValues('pcDescontoAdicional');
  const valorDescontoAdicionalDefault = getValues('valorDescontoAdicional');

  const {
    register,
    formState: { errors: { disciplinas: disciplinasErros } },
    control
  } = useFormContext<MatriculaAlunoSchema>();

  const values = getValues();
  const turmaId = watch('turmaId');

  useUpdateEffect(() => {
    // Guarda contra o re-render causado pelo próprio setEspecificasTable da hydration
    if (justHydrated.current) {
      justHydrated.current = false;
      return;
    }

    let disciplinas = getValues('disciplinas');
    let newSelectedRowKeys: React.Key[] = [];
    let selectedRows: EspecificaTableType[] = [];
    if (especificasTable.length > 0) {
      disciplinas?.forEach((disciplina: any, index: number) => {
        disciplina.key = disciplina.codEspecificaServico;
        let especificaToTabel = especificasTable?.filter(item => item.key === disciplina?.codEspecificaServico)[0];
        newSelectedRowKeys.push(disciplina.codEspecificaServico);
        selectedRows.push(especificaToTabel);
      });
      if (isHydrating.current) {
        // Modo edição: restaurar seleção visual sem recalcular descontos
        isHydrating.current = false;
        const keys = newSelectedRowKeys.filter(k =>
          disciplinas.find((d: any) => d.codEspecificaServico === k && d.tipo !== 'MATRICULA')
        );
        setSelectedRowKeys(keys);
        const selectedOnHydration = especificasTable.filter(item => keys.includes(item.key));
        // Restaurar estado de cabine: o botão fica visível quando já existe número
        // salvo OU quando o serviço Cabine Vip está contratado mas ainda sem número
        // definido — só assim é possível informá-lo depois, inclusive já matriculado.
        const numeroCabineValue = getValues('estudante.numeroCabine');
        if (numeroCabineValue) {
          setValue('numeroCabine', numeroCabineValue);
          setNumeroCabine(numeroCabineValue);
        }
        if (numeroCabineValue || selectedOnHydration.some(item => item.tipo === 'CABINEVIP')) {
          setEnableCabine(true);
        }
        // Restaurar estado de armário (mesma regra da cabine)
        const numeroArmarioValue = getValues('estudante.numeroArmario');
        if (numeroArmarioValue) {
          setValue('numeroArmario', numeroArmarioValue);
          setNumeroArmario(numeroArmarioValue);
        }
        if (numeroArmarioValue || selectedOnHydration.some(item => item.tipo === 'ARMARIO')) {
          setEnableArmario(true);
        }
        // Aplicar disableCheck correto sem recalcular descontos:
        // 1) Habilita todos (desfaz o disableCheck=true inicial de CABINEVIP/ORIENTACAO/ARMARIO)
        especificasTable.forEach(item => { item.disableCheck = false; });
        // 2) Desabilita complementares cujo pai está selecionado
        selectedOnHydration.forEach(item => {
          if (item.tipo !== "ESPECIFICA") return;
          const relacionadas = disciplinasType?.especificas.find(e => e.id === item.disciplinaId)?.especificasRelacionadas;
          if (!relacionadas) return;
          const ids = relacionadas.split(";").filter(Boolean).map(id => Number(id));
          especificasTable.forEach(row => {
            if (ids.includes(row.disciplinaId) && row.tipo === "ESPECIFICA") {
              row.disableCheck = true;
            }
          });
        });
        justHydrated.current = true;
        setEspecificasTable([...especificasTable]);
        return;
      }
      // Novo cadastro: pré-selecionar rows com defaultChecked
      if (!disciplinas || disciplinas.length === 0) {
        const rowsPadrao = especificasTable.filter(item => item.selecionado);
        if (rowsPadrao.length > 0) {
          onSelectChange(rowsPadrao.map(item => item.key), rowsPadrao);
          return;
        }
      }
      if (selectedRows.length > 0) {
        onSelectChange(newSelectedRowKeys, selectedRows);
      }
    }
  }, [especificasTable]);

  useEffect(() => {
    // Ao trocar de turma (não na carga inicial), limpa a seleção de disciplinas para
    // recarregar a tabela com os dados da nova turma. Para novo cadastro (sem `id` na rota)
    // sempre recarrega. Para edição, só recarrega quando o status NÃO é Matrícula
    // (tipoMatricula '1'), permitindo livre troca em Orçamento ('5') e Pré-Matrícula ('4').
    const turmaAnterior = previousTurmaIdRef.current;
    const turmaMudou = turmaAnterior !== undefined && turmaAnterior !== turmaId;
    const tipoMatricula = getValues('tipoMatricula');
    const isNovoCadastro = !id;
    if (turmaMudou && (isNovoCadastro || tipoMatricula !== '1')) {
      setValue('disciplinas', []);
      setSelectedRowKeys([]);
      setEspecificasTable([]);
      setCountDescontoSelected(0);
      setTaxaMatricula(0);
      setTaxaMatriculaSemDesconto(0);
      setPossuiRelacionadasSelecionado(false);
      setEnableCabine(false);
      setEnableArmario(false);
      setDescontoMatriculaManual(null);
      setEditandoDescontoMatricula(false);
      limparCabine();
      limparArmario();
      isHydrating.current = false;
    }
    previousTurmaIdRef.current = turmaId;

    getTurma(turmaId?.toString() ?? '');
    let pcDesconto = getValues('pcDescontoAdicional');
    if (pcDesconto) {
      setPcDescontoAdicional(pcDesconto);
      setDisableValorDesconto(true);
    }
    let valorDesconto = getValues('valorDescontoAdicional');
    if (valorDesconto) {
      setValorDescontoAdicional(valorDesconto);
      setDisablePcDesconto(true);
    }
  }, [turmaId]);

  const getTurma = async (e: string) => {
    const turmaId = e;
    if (!turmaId) {
      return;
    }
    const data: any = await getTurmaById(turmaId);

    setDisciplinasType(data?.data);

    const listaEspecificas: EspecificaTableType[] = [];
    data?.data?.especificas.forEach((especifica: EspecificaType) => {
      let especificasTurma: EspecificaTableType = {
        key: "E" + especifica.id,
        disciplinaId: especifica.id,
        nome: especifica.nome,
        valor: especifica.valor,
        percDesconto: 0,
        valorDesconto: 0,
        valorFinal: 0,
        tipo: "ESPECIFICA",
        disableCheck: false,
        selecionado: !!especifica.defaultChecked
      };
      listaEspecificas.push(especificasTurma);
    });
    data?.data?.servicos.forEach((servico: ServicoType) => {
      if (servico.tipo === "MATRICULA") {
        return;
      }

      let especificasTurma: EspecificaTableType = {
        key: "S" + servico.id,
        disciplinaId: servico.id,
        nome: servico.nome,
        valor: servico.valor,
        percDesconto: 0,
        valorDesconto: 0,
        valorFinal: 0,
        tipo: servico.tipo,
        disableCheck: (servico.tipo === "CABINEVIP" || servico.tipo === "ORIENTACAO" || servico.tipo === "ARMARIO") ? true : false,
        selecionado: !!servico.defaultChecked
      };
      listaEspecificas.push(especificasTurma);
    });
    // Mesclar descontos salvos (modo edição)
    const disciplinasSalvas: any[] = getValues('disciplinas') ?? [];
    if (disciplinasSalvas.length > 0) {
      listaEspecificas.forEach(item => {
        const salva = disciplinasSalvas.find((d: any) => d.codEspecificaServico === item.key);
        if (salva) {
          item.valor = salva.valor ?? item.valor;
          item.percDesconto = sanitizeNumber(salva.percDesconto);
          item.valorDesconto = salva.valorDesconto ?? 0;
          item.valorFinal = salva.valorFinal ?? 0;
          item.descontoManual = salva.descontoManual ?? false;
          item.selecionado = true;
        } else {
          // Modo edição: a matrícula salva é a única fonte da verdade da seleção.
          // O "Marcado Padrão" (defaultChecked) da Específica/Serviço só vale para
          // cadastro novo — se ficasse marcado aqui, a linha entraria no recálculo
          // do desconto adicional (aplicarDesconto -> atualizaTable) mesmo com o
          // checkbox desmarcado, inflando o Valor Total.
          item.selecionado = false;
          item.percDesconto = 0;
          item.valorDesconto = 0;
          item.valorFinal = 0;
          item.descontoManual = false;
        }
      });
      // Restaurar taxa de matrícula a partir da disciplina salva
      // A matrícula é salva com codEspecificaServico = "S" + servicoMatricula.id
      const servicoMatriculaTurma = data?.data?.servicos.find((s: any) => s.tipo === 'MATRICULA');
      const matriculaKey = servicoMatriculaTurma ? 'S' + servicoMatriculaTurma.id : null;
      const matriculaSalva = matriculaKey
        ? disciplinasSalvas.find((d: any) => d.codEspecificaServico === matriculaKey)
        : disciplinasSalvas.find((d: any) => d.nome?.toLowerCase().includes('matrícula') || d.nome?.toLowerCase().includes('matricula'));
      if (matriculaSalva) {
        const valorFinalMatriculaSalva = sanitizeNumber(matriculaSalva.valorFinal);
        // O valor bruto da matrícula é o `valor` salvo; a soma valorFinal + valorDesconto
        // fica só como fallback para registros antigos gravados sem o valor bruto.
        const valorBrutoMatriculaSalva = sanitizeNumber(matriculaSalva.valor) > 0
          ? sanitizeNumber(matriculaSalva.valor)
          : valorFinalMatriculaSalva + sanitizeNumber(matriculaSalva.valorDesconto);
        setTaxaMatricula(valorFinalMatriculaSalva);
        setTaxaMatriculaSemDesconto(valorBrutoMatriculaSalva);
      }
      isHydrating.current = true;
    }

    setEspecificasTable(listaEspecificas);
    let disciplinas = getValues('disciplinas');
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: EspecificaTableType[]) => {

    const previousKeys = selectedRowKeys;
    const removedKeys = previousKeys.filter(key => !newSelectedRowKeys.includes(key));
    const addedKeys = newSelectedRowKeys.filter(key => !previousKeys.includes(key));

    if (removedKeys.length > 0) {
      const removedKey = removedKeys[0];
      const removedItem = especificasTable.find(item => item.key === removedKey);

      if (removedItem && removedItem.tipo === "ESPECIFICA") {
        const monitoriaEspecifica = disciplinasType?.servicos.find(
          monitoria => monitoria.tipo === "MONITORIA" &&
            monitoria.id === disciplinasType?.especificas.find(
              e => e.id === removedItem.disciplinaId
            )?.servicoVinculado?.id
        );

        if (monitoriaEspecifica) {
          const monitoriaKey = "S" + monitoriaEspecifica.id;
          newSelectedRowKeys = newSelectedRowKeys.filter(key => key !== monitoriaKey);
          selectedRows = selectedRows.filter(row => row.key !== monitoriaKey);
        }
      }

      selectedRows = selectedRows.filter(row => row.key !== removedKey);
    }

    especificasTable.forEach(notEspecific => {
      // Não zera linhas com desconto manual travado se ainda estiverem selecionadas
      const continuaSelecionada = newSelectedRowKeys.includes(notEspecific.key);
      if (notEspecific.descontoManual && continuaSelecionada) {
        notEspecific.disableCheck = false;
        return;
      }
      notEspecific.percDesconto = 0;
      notEspecific.valorDesconto = 0;
      notEspecific.valorFinal = 0;
      notEspecific.disableCheck = false;
      notEspecific.selecionado = false;
      notEspecific.descontoManual = false;
    });

    selectedRows = selectedRows.filter(s => s != undefined);
    if (selectedRows.length > 0) {
      let possuiRelacionadas = false;
      selectedRows.forEach(item => {
        let especificaRow = disciplinasType?.especificas.filter(especifica => especifica.id === item.disciplinaId && item.tipo === "ESPECIFICA")[0];
        possuiRelacionadas = especificaRow?.especificasRelacionadas != undefined && especificaRow?.especificasRelacionadas!.length > 0;

        if (possuiRelacionadas) {
          setPossuiRelacionadasSelecionado(!possuiRelacionadasSelecionado);
          let relacionadas = disciplinasType?.especificas.filter(especifica => especifica.id === item.disciplinaId && item.tipo === "ESPECIFICA")[0].especificasRelacionadas;
          if (relacionadas) {
            let especificasRelacionadasIds = relacionadas.split(";").filter(Boolean).map(id => Number(id));
            let rowsRelacionadas = especificasTable.filter(item => 
              especificasRelacionadasIds.includes(item.disciplinaId) && item.tipo === "ESPECIFICA"
            );
            rowsRelacionadas.forEach(row => {
              row.disableCheck = true;
            });
          }
        }
      });

      let newlyAddedRows = selectedRows.filter(row => addedKeys.includes(row.key));
      newlyAddedRows.forEach(newRow => {
        let especificaSelecionada = disciplinasType?.especificas.filter(
          especifica => especifica.id === newRow.disciplinaId && newRow.tipo === "ESPECIFICA"
        )[0];
        let monitoriaEspecifica = disciplinasType?.servicos.filter(
          monitoria => monitoria.tipo === "MONITORIA" && monitoria.id === especificaSelecionada?.servicoVinculado?.id
        )[0];
        if (monitoriaEspecifica) {
          let keyComplementar = "S" + monitoriaEspecifica?.id;
          if (!removedKeys.includes(keyComplementar) && selectedRows.filter(item => item.key === keyComplementar).length === 0) {
            newSelectedRowKeys.push(keyComplementar);
            let monitoriaSelecao = especificasTable.filter(item => item.key === keyComplementar)[0];
            monitoriaSelecao.disableCheck = false;
            selectedRows.push(monitoriaSelecao);
          }
        }
      });
    }
    atualizaTable(selectedRows);
    setSelectedRowKeys(newSelectedRowKeys);

    if (selectedRows.length > 0) {
      // Verificar se há Cabine Vip nas seleções
      const temCabineVipSelecionada = selectedRows.some(item => item?.tipo === "CABINEVIP");
      const cabineJaPreenchida = getValues('estudante.numeroCabine');

      if (temCabineVipSelecionada) {
        setEnableCabine(true);
        if (!cabineJaPreenchida) {
          setOpenCabine(true);
        }
      } else {
        if (enableCabine) {
          limparCabine();
          setEnableCabine(false);
        }
      }

      // Verificar se há Armário nas seleções
      const temArmarioSelecionado = selectedRows.some(item => item?.tipo === "ARMARIO");
      const armarioJaPreenchido = getValues('estudante.numeroArmario');

      if (temArmarioSelecionado) {
        setEnableArmario(true);
        if (!armarioJaPreenchido) {
          setOpenArmario(true);
        }
      } else {
        if (enableArmario) {
          limparArmario();
          setEnableArmario(false);
        }
      }
    }
  };

  /**
   * `descontoMatriculaOverride` permite recalcular já com o desconto de matrícula
   * recém-digitado, sem esperar o setState de `descontoMatriculaManual`.
   */
  const atualizaTable = (selectedRows: EspecificaTableType[], descontoMatriculaOverride?: number | null) => {
    setValue('disciplinas', []);
    let servicoMatricula = disciplinasType?.servicos.filter(monitoria => monitoria.tipo === "MATRICULA")[0];
    let taxaMatriculaFinal = 0;
    let valorMatricula = 0;

    if (selectedRows.length > 0) {

      let countSelected = 0;
      let cabineVipEOrientacao = selectedRows.filter(item => item.tipo === "CABINEVIP" || item.tipo === "ORIENTACAO").length === 2;

      selectedRows.forEach(item => {
        let selectedEspecifica = disciplinasType?.especificas.filter(especifica => especifica.id === item.disciplinaId && item.tipo === "ESPECIFICA")[0];
        if (selectedEspecifica?.aplicaDescontoTurma) {
          countSelected = countSelected + 1;
          setCountDescontoSelected(countSelected);
        }
      });

      if (servicoMatricula) {
        const percDescontoMatricula = disciplinasType?.percDescontoMatricula ?? 0;
        valorMatricula = (countSelected < 2 ? servicoMatricula.valor * (1 - (percDescontoMatricula / 100)) : countSelected * servicoMatricula.valorDesconto * (1 - (percDescontoMatricula / 100)));
        setTaxaMatricula(valorMatricula);
        setTaxaMatriculaSemDesconto(valorMatricula);
      }

      selectedRows.forEach(item => {
        let selected = disciplinasType?.especificas.filter(especifica => especifica.id === item.disciplinaId)[0];
        // Linha com desconto manual: o valor informado pelo usuário substitui o
        // desconto por faixa da turma e vira a base da linha. Restaura sempre a
        // partir de `valorDescontoManual` para o desconto adicional aplicado
        // depois não se acumular a cada novo "Aplicar".
        if (item.descontoManual) {
          const descontoManualBase = Math.min(
            Math.max(sanitizeNumber(item.valorDescontoManual ?? item.valorDesconto), 0),
            item.valor
          );
          item.valorDescontoManual = descontoManualBase;
          item.valorDesconto = descontoManualBase;
          item.valorFinal = item.valor - descontoManualBase;
          item.percDesconto = safePercentage(descontoManualBase, item.valor);
          item.selecionado = true;
          return;
        }
        if ((countSelected < 6 || (countSelected >= 6 && !cabineVipEOrientacao)) && item.tipo != "MONITORIA") {
          let desconto = disciplinasType?.descontos.filter(desconto => Number(desconto.tipoDesconto.replace("Especifica", "")) == countSelected)[0];
          item.percDesconto = item.tipo === 'SIMULADO' ? 0 : (desconto?.percentualDesconto ?? 0);
          // Cap: para CABINEVIP/ORIENTACAO, o desconto nunca pode ultrapassar o configurado
          // como "CabineEOrientacao" no cadastro da Turma.
          if (item.tipo === "CABINEVIP" || item.tipo === "ORIENTACAO") {
            const descontoCabineCap = disciplinasType?.descontos.filter(d => d.tipoDesconto === "CabineEOrientacao")[0];
            if (descontoCabineCap && item.percDesconto > descontoCabineCap.percentualDesconto) {
              item.percDesconto = descontoCabineCap.percentualDesconto;
            }
          }
          item.valorDesconto = item.valor * item.percDesconto / 100;
          item.valorFinal = item.valor - item.valorDesconto;
        } else if (countSelected >= 6 && cabineVipEOrientacao && item.tipo != "MONITORIA") {
          let desconto = disciplinasType?.descontos.filter(desconto => desconto.tipoDesconto == "Completo")[0];
          item.percDesconto = desconto?.percentualDesconto ?? 0;
          item.valorDesconto = item.valor * (desconto?.percentualDesconto ?? 0) / 100;
          item.valorFinal = item.valor - item.valorDesconto;
          if (item.key.toString().includes("S") &&
            (item.tipo.includes("CABINEVIP") || item.tipo.includes("ORIENTACAO"))) {
            let descontoCabine = disciplinasType?.descontos.filter(desconto => desconto.tipoDesconto == "CabineEOrientacao")[0];
            item.percDesconto = item.tipo === 'SIMULADO' ? 0 : (descontoCabine?.percentualDesconto ?? 0);
            item.valorDesconto = item.valor * (item.tipo === 'SIMULADO' ? 0 : (descontoCabine?.percentualDesconto ?? 0)) / 100;
            item.valorFinal = item.valor - (item.valor * (item.tipo === 'SIMULADO' ? 0 : (descontoCabine?.percentualDesconto ?? 0)) / 100);
          }
        }
        if (item.tipo === "MONITORIA") {
          let monitoriaEspecifica = disciplinasType?.servicos.filter(monitoria => monitoria.id === item.disciplinaId)[0];
          let especificasVinculadas = disciplinasType?.especificas.filter(e => e.servicoVinculado?.id === item.disciplinaId);
          let keyEspecifica = "E" + especificasVinculadas?.find(especifica =>
            selectedRows.some(row => row.disciplinaId === especifica.id)
          )?.id;
          let possuiEspecificaSelecionada = selectedRows.filter(e => e.key === keyEspecifica).length > 0;
          item.valorDesconto = possuiEspecificaSelecionada ? monitoriaEspecifica?.valorDesconto ?? 0 : 0;
          item.valorFinal = possuiEspecificaSelecionada ? item.valor - (monitoriaEspecifica?.valorDesconto ?? 0) : item.valor;
          item.percDesconto = safePercentage(item.valorDesconto, item.valor);
        }

        item.selecionado = true;
      });

      // A matrícula entra na base pelo valor já líquido do desconto manual dela,
      // igual às linhas da tabela — assim o desconto adicional é rateado sobre o
      // que de fato ainda se paga.
      const descontoMatriculaAtual = descontoMatriculaOverride !== undefined
        ? descontoMatriculaOverride
        : descontoMatriculaManual;
      const descontoMatriculaBase = descontoMatriculaAtual !== null
        ? Math.min(Math.max(descontoMatriculaAtual, 0), valorMatricula)
        : 0;
      const valorMatriculaBase = valorMatricula - descontoMatriculaBase;

      let valorTotalOriginal = selectedRows.reduce((a, v) => a = a + v.valorFinal, 0) + valorMatriculaBase;

      let pcDesconto = (!valorDescontoAdicional && valorDescontoAdicional === 0) ? pcDescontoAdicional : safePercentage(valorDescontoAdicional, valorTotalOriginal);

      // O desconto adicional é rateado sobre TODAS as linhas selecionadas,
      // inclusive as de desconto manual — antes elas ficavam de fora e o valor
      // digitado no Desconto Adicional acabava aplicado só na matrícula.
      selectedRows.forEach(especifica => {
        if (especifica.valorFinal > 0) {
          especifica.valorFinal = especifica.valorFinal - (especifica.valorFinal * pcDesconto / 100);
          especifica.valorDesconto = especifica.valor - especifica.valorFinal;
          especifica.percDesconto = safePercentage(especifica.valorDesconto, especifica.valor);
        }
      });

      taxaMatriculaFinal = valorMatriculaBase - (valorMatriculaBase * pcDesconto / 100);
      setTaxaMatricula(taxaMatriculaFinal);
    } else {
      // Sem nenhuma disciplina selecionada não há taxa de matrícula: zera os
      // estados para o resumo não continuar somando a matrícula anterior no Total.
      setTaxaMatricula(0);
      setTaxaMatriculaSemDesconto(0);
      setDescontoMatriculaManual(null);
    }

    for (let index = 0; index < selectedRows.length; index++) {
      setValue(`disciplinas.${index}.codEspecificaServico`, selectedRows[index].key.toString());
      setValue(`disciplinas.${index}.disciplinaId`, selectedRows[index].disciplinaId);
      setValue(`disciplinas.${index}.tipo`, selectedRows[index].tipo);
      setValue(`disciplinas.${index}.nome`, selectedRows[index].nome);
      setValue(`disciplinas.${index}.valor`, selectedRows[index].valor);
      setValue(`disciplinas.${index}.percDesconto`, sanitizeNumber(selectedRows[index].percDesconto));
      setValue(`disciplinas.${index}.valorDesconto`, selectedRows[index].valorDesconto);
      setValue(`disciplinas.${index}.valorFinal`, selectedRows[index].valorFinal);
      setValue(`disciplinas.${index}.descontoManual`, selectedRows[index].descontoManual ?? false);
    }

    const matriculaIdx = selectedRows.length;
    setValue(`disciplinas.${matriculaIdx}.codEspecificaServico`, 'S' + servicoMatricula?.id);
    setValue(`disciplinas.${matriculaIdx}.tipo`, servicoMatricula?.tipo);
    setValue(`disciplinas.${matriculaIdx}.nome`, servicoMatricula?.nome);
    setValue(`disciplinas.${matriculaIdx}.valor`, valorMatricula);
    setValue(
      `disciplinas.${matriculaIdx}.percDesconto`,
      safePercentage((valorMatricula ?? 0) - taxaMatriculaFinal, valorMatricula ?? 0)
    );
    setValue(`disciplinas.${matriculaIdx}.valorDesconto`, (valorMatricula ?? 0) - taxaMatriculaFinal);
    setValue(`disciplinas.${matriculaIdx}.valorFinal`, taxaMatriculaFinal);

    if (getValues('estudante.numeroCabine')) {
      const numeroCabineValue = getValues('estudante.numeroCabine');
      setValue('numeroCabine', numeroCabineValue);
      setNumeroCabine(numeroCabineValue);
      setEnableCabine(true);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: EspecificaTableType) => ({
      disabled: disabled || record.disableCheck === true,
      name: record.nome,
    }),
    hideSelectAll: true,
  };

  const pcDescontoChange: InputNumberProps['onChange'] = (value) => {
    if (value === null) {
      return;
    }
    const pcDesconto = Number(value.toString().replace('%', '').replace(',', '.'));
    setPcDescontoAdicional(pcDesconto);
    setValue('pcDescontoAdicional', pcDesconto);
    if (pcDesconto && pcDesconto > 0) {
      setDisableValorDesconto(true);
    } else {
      setDisableValorDesconto(false);
    }
  };

  const valorDescontoChange: InputNumberProps['onChange'] = (value) => {
    if (value === null) {
      return;
    }
    const valorDesconto = Number(value.toString().replace('R$', '').replace('.', '').replace(',', '.'));
    setValorDescontoAdicional(valorDesconto);
    setValue('valorDescontoAdicional', valorDesconto);
    if (valorDesconto && valorDesconto > 0) {
      setDisablePcDesconto(true);
    } else {
      setDisablePcDesconto(false);
    }
  };

  const aplicarDesconto = () => {
    // A seleção visível (selectedRowKeys) é a fonte da verdade — recalcular sobre
    // `selecionado` deixava entrar linha desmarcada na tabela e no Valor Total.
    const especificasSelecionadas = especificasTable.filter(item => selectedRowKeys.includes(item.key));
    atualizaTable(especificasSelecionadas);
  };

  const limparCabine = () => {
    setValue('estudante.numeroCabine', undefined);
    setValue('numeroCabine', undefined);
    setNumeroCabine(undefined);
  };

  const limparArmario = () => {
    setValue('estudante.numeroArmario', undefined);
    setValue('numeroArmario', undefined);
    setNumeroArmario(undefined);
  };

  const onCreateArmario = () => {
    const numeroArmarioValue = getValues('numeroArmario');
    const numeroArmarioNumerico = numeroArmarioValue && numeroArmarioValue !== '' ? Number(numeroArmarioValue) : undefined;
    setValue('estudante.numeroArmario', numeroArmarioNumerico);
    setNumeroArmario(numeroArmarioNumerico || undefined);
    setOpenArmario(false);
  };

  const openModalArmario = () => {
    setOpenArmario(true);
  };

  const habilitaDescontoAdicional = () => {
    setDescontoAdicionalHabilitado(!descontoAdicionalHabilitado);
  };

  const handleSaveDescontoMatricula = (rawValue: string) => {
    const novoDesconto = Number(
      rawValue.replace(/R\$\s*/g, '').replace(/\./g, '').replace(',', '.')
    ) || 0;
    const novoDescontoLimitado = Math.min(Math.max(novoDesconto, 0), taxaMatriculaSemDesconto);
    setDescontoMatriculaManual(novoDescontoLimitado);
    setEditandoDescontoMatricula(false);
    // Recalcula pelo caminho normal para o desconto adicional vigente continuar
    // rateado sobre a matrícula já líquida do desconto manual.
    atualizaTable(
      especificasTable.filter(item => selectedRowKeys.includes(item.key)),
      novoDescontoLimitado
    );
  };

  const selecionarTodos = () => {
    const todosExcetoRedacao = especificasTable.filter(item => !item.nome.includes("Redação"))
      .map(item => item.key);
    onSelectChange(todosExcetoRedacao, especificasTable.filter(item => !item.nome.includes("Redação")));
  };

  const desmarcarTodos = () => {
    onSelectChange([], []);
    limparCabine();
    limparArmario();
  };

  const printOrcamento = () => {
    if (!values ||
      !values.responsavel.pessoaResponsavel.nome ||
      !values.responsavel.pessoaResponsavel.celular ||
      !values.responsavel.pessoaResponsavel.email ||
      !(values.disciplinas.length > 0)
    ) {
      Modal.warning({
        title: 'Orçamento',
        content: 'Preencha todos os campos obrigatórios para gerar o orçamento. Dados de responsável e ao menos uma disciplina são obrigatórios.',
        okButtonProps: { style: { backgroundColor: 'blue' } }
      });
      return;
    }
    // Popular unidade no form a partir do localStorage
    const unitData = JSON.parse(localStorage.getItem("UnidadeLogada") as any);
    if (unitData) {
      setValue('unidade', unitData);
    }

    const userData = JSON.parse(localStorage.getItem("@MatriculaWeb:user") as any);
    if (userData) {
      setValue('usuarioNome', userData.usuario.nome);
    }

    if (!values.turmaNome) {
      const turmaSelecionada = disciplinasType?.nome;
      setValue('turmaNome', turmaSelecionada);
    }

    setParcelasSelecionadas('avista');
    setIsModalParcelasOpen(true);
  }

  const handleOk = () => {
    setIsModalOrcamentoOpen(false);
  };

  const handleCancel = () => {
    setIsModalOrcamentoOpen(false);
  };

  const handleConfirmarParcelas = () => {
    const disciplinas = getValues('disciplinas') ?? [];
    const totalSemDesconto = disciplinas.reduce((a: number, v: any) => a + (v.valor || 0), 0);
    const totalComDesconto = disciplinas.reduce((a: number, v: any) => a + (v.valorFinal || 0), 0);
    const qtdParcelas = parcelasSelecionadas === 'avista' ? 1 : Math.max(Number(parcelasSelecionadas) || 1, 1);

    setParcelasOrcamento({
      qtdParcelas,
      totalSemDesconto,
      parcelaSemDesconto: totalSemDesconto / qtdParcelas,
      totalComDesconto,
      parcelaComDesconto: totalComDesconto / qtdParcelas,
    });
    setIsModalParcelasOpen(false);
    setIsModalOrcamentoOpen(true);
  };

  const onCreate = () => {
    const numeroCabineValue = getValues('numeroCabine');
    // Se o campo estiver vazio ou for string vazia, passar null, senão converter para número
    const numeroCabineNumerico = numeroCabineValue && numeroCabineValue !== '' ? Number(numeroCabineValue) : undefined;
    
    setValue('estudante.numeroCabine', numeroCabineNumerico);
    setNumeroCabine(numeroCabineNumerico || undefined); // Converter null para undefined
    setOpenCabine(false);
  };

  const openModalCabine = () => {
    setOpenCabine(true);
  }

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Específicas / Serviços</span>
        <Container>
          <div className="mt-8">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <FormSearchInput
                    id="turma"
                    name="turmaId"
                    property="nome"
                    optionUrl={API_CONSTANT_MAP.turma.concat("?Ativo=true")}
                    inputValueName="nome"
                    valueProperty="id"
                    label="Turma"
                    onChangeCallback={getTurma}
                    disabled={disabled}
                  />
                </div>
              </div>
              <div className="flex items-end gap-4">
                <Button onClick={selecionarTodos} type="primary" className="mb-[2px]" disabled={disabled}>
                  Selecionar Todos
                </Button>
                <Button onClick={desmarcarTodos} type="primary" className="mb-[2px]" disabled={disabled}>
                  Desmarcar Todos
                </Button>
              </div>
            </div>
            <Divider />
            <div className="grid ">
              <div>
                <Table<EspecificaTableType>
                  components={components}
                  rowClassName={() => 'editable-row'}
                  rowSelection={rowSelection}
                  columns={columns as ColumnsType<EspecificaTableType>}
                  dataSource={especificasTable}
                  pagination={false}
                  summary={(pageData) => {
                    let totalValor = 0;
                    let totalDesconto = 0;
                    let totalValorDesconto = 0;
                    let totalFinal = 0;

                    pageData.forEach(({ valor, valorDesconto, valorFinal }) => {
                      totalValor += valorFinal + valorDesconto;
                      totalValorDesconto += valorDesconto;
                      totalFinal += valorFinal;
                    });

                    // O desconto da matrícula também compõe o total: sem ele o
                    // "Desconto" e o "% Desconto" da linha Total ficavam menores
                    // que o desconto realmente concedido.
                    const descontoMatriculaResumo = Math.max(taxaMatriculaSemDesconto - taxaMatricula, 0);
                    totalValor = totalValor + taxaMatriculaSemDesconto;
                    totalFinal = totalFinal + taxaMatricula;
                    totalValorDesconto = totalValorDesconto + descontoMatriculaResumo;
                    totalDesconto = safePercentage(totalValorDesconto, totalValor);
                    let pcDescontoMatricula = safePercentage(descontoMatriculaResumo, taxaMatriculaSemDesconto);
                    setValorFinal(totalFinal);

                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <Text>Matrícula</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align='right'>
                            <Text strong>{taxaMatriculaSemDesconto.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align='right'>{pcDescontoMatricula.toLocaleString('pt-br', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align='right'>
                            {editandoDescontoMatricula && !disabled ? (
                              <Input
                                autoFocus
                                defaultValue={(taxaMatriculaSemDesconto - taxaMatricula).toLocaleString('pt-br', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                onPressEnter={(e) => handleSaveDescontoMatricula((e.target as HTMLInputElement).value)}
                                onBlur={(e) => handleSaveDescontoMatricula(e.target.value)}
                                style={{ textAlign: 'right' }}
                              />
                            ) : (
                              <div
                                className="editable-cell-value-wrap"
                                style={{ paddingInlineEnd: 24, cursor: disabled ? 'default' : 'pointer' }}
                                onClick={() => { if (!disabled) setEditandoDescontoMatricula(true); }}
                              >
                                <Text strong>{(taxaMatriculaSemDesconto - taxaMatricula).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Text>
                              </div>
                            )}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align='right'>
                            <Text>{taxaMatricula.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}><Typography.Title level={4}>Total</Typography.Title></Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align='right'>
                            <Text strong>{totalValor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align='right'>
                            <Text strong>{totalDesconto.toLocaleString('pt-br', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align='right'>
                            <Text strong>{totalValorDesconto.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align='right'>
                            <Text style={{ color: 'yellow' }} strong>{totalFinal.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                />
              </div>
              <Divider></Divider>
              <div>
                <Row gutter={[16, 24]}>
                  <Col md={24} lg={1}>
                    <Button type="primary" shape="circle" onClick={habilitaDescontoAdicional} disabled={disabled}>{descontoAdicionalHabilitado ? "+" : "-"}</Button>
                  </Col>
                  <Col md={24} lg={10} hidden={descontoAdicionalHabilitado}>
                    <label onClick={habilitaDescontoAdicional}>Desconto Adicional</label>
                  </Col>
                  <Col md={24} lg={3} hidden={descontoAdicionalHabilitado}>
                    <Form.Item
                      name="pcDescontoAdicional"
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <MaskedInput
                        mask={percentMask}
                        className="ant-input"
                        placeholder="% 00,00"
                        value={pcDescontoAdicional}
                        defaultValue={pcDescontoAdicionalDefault}
                        onChange={(e) => pcDescontoChange(e.target.value)}
                        disabled={disabled || disablePcDesconto}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={24} lg={1} hidden={descontoAdicionalHabilitado}></Col>
                  <Col md={24} lg={4} hidden={descontoAdicionalHabilitado}>
                    <Form.Item
                      name="valorDescontoAdicional"
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <MaskedInput
                        mask={currencyMask}
                        className="ant-input"
                        placeholder="R$ 0,00"
                        value={valorDescontoAdicional}
                        defaultValue={valorDescontoAdicionalDefault}
                        onChange={(e) => valorDescontoChange(e.target.value)}
                        disabled={disabled || disableValorDesconto}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={24} lg={3} hidden={descontoAdicionalHabilitado}>
                    <Button type="link" onClick={aplicarDesconto} disabled={disabled}>Aplicar</Button>
                  </Col>
                </Row>
                <Row gutter={[16, 24]}>
                  <Col md={12} lg={6}>
                    <Button type='primary' onClick={printOrcamento} disabled={disabled}>Imprimir Orçamento</Button>
                  </Col>
                  {/*
                    Exceção ao bloqueio do aluno já matriculado: o número da Cabine
                    Vip e o do Armário continuam editáveis, pois são apenas a
                    alocação física do aluno e não alteram disciplinas nem valores.
                    Todo o restante deste passo permanece somente leitura.
                  */}
                  <Col md={6} lg={6} hidden={!enableCabine}>
                    <Button type='primary' onClick={openModalCabine}>Cabine Vip: {numeroCabine}</Button>
                    <Button type="link" onClick={limparCabine}>Limpar Cabine</Button>
                  </Col>
                  <Col md={6} lg={6} hidden={!enableArmario}>
                    <Button type='primary' onClick={openModalArmario}>Armário: {numeroArmario}</Button>
                    <Button type="link" onClick={limparArmario}>Limpar Armário</Button>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>
        <Modal
          title="Selecionar Parcelas"
          open={isModalParcelasOpen}
          onOk={handleConfirmarParcelas}
          onCancel={() => setIsModalParcelasOpen(false)}
          okText="Confirmar"
          cancelText="Cancelar"
          width={360}
        >
          <div style={{ margin: '16px 0' }}>
            <label style={{ display: 'block', marginBottom: 8 }}>Quantidade de parcelas:</label>
            <Select
              style={{ width: '100%' }}
              value={parcelasSelecionadas}
              onChange={setParcelasSelecionadas}
              options={[
                { value: 'avista', label: 'À Vista' },
                ...Array.from({ length: 14 }, (_, i) => ({
                  value: String(i + 1),
                  label: `${i + 1}x`,
                })),
              ]}
            />
          </div>
        </Modal>
        <Modal
          title="Orçamento"
          open={isModalOrcamentoOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          width={'80%'}>
          <Orcamento matriculaAluno={getValues()} parcelasInfo={parcelasOrcamento} />
        </Modal>
        <Modal
          open={openCabine}
          title="Cabine Vip"
          okText="Confirmar"
          cancelText="Cancelar"
          okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
          onCancel={() => setOpenCabine(false)}
          destroyOnClose
          modalRender={(dom) => (
            <Form
              layout="vertical"
              form={form}
              name="form_in_modal"
              onFinish={() => onCreate()}
            >
              {dom}
            </Form>
          )}
        >
          <Form.Item name="modifier" className="collection-create-form_last-form-item">
            <FormSearchInput
              id="nrCabine"
              name="numeroCabine"
              property="numeroCabine"
              optionUrl={API_CONSTANT_MAP.aluno.concat("/cabines/" + (id ?? 0))}
              inputValueName="numeroCabine"
              valueProperty="id"
              label="Cabine"
            />
          </Form.Item>
        </Modal>
        <Modal
          open={openArmario}
          title="Armário"
          okText="Confirmar"
          cancelText="Cancelar"
          okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
          onCancel={() => setOpenArmario(false)}
          destroyOnClose
          modalRender={(dom) => (
            <Form
              layout="vertical"
              form={form2}
              name="form_armario_modal"
              onFinish={() => onCreateArmario()}
            >
              {dom}
            </Form>
          )}
        >
          <Form.Item name="modifier" className="collection-create-form_last-form-item">
            <FormSearchInput
              id="nrArmario"
              name="numeroArmario"
              property="numeroArmario"
              optionUrl={API_CONSTANT_MAP.aluno.concat("/armarios/" + (id ?? 0))}
              inputValueName="numeroArmario"
              valueProperty="id"
              label="Armário"
            />
          </Form.Item>
        </Modal>
      </div>
    </>
  )
}

export default Disciplinas;